import { useEffect, useReducer, useCallback } from 'react'
import api, { IEmployee } from '@api'
import { Toaster } from '@ui'
import { ApolloError } from 'apollo-boost'
import { useShouldReload } from '@hooks/reloadContext'
import { debounce } from 'lodash'

enum Actions {
  LOAD_START,
  LOAD_SUCCESS,
  SEARCH,
  LOAD_MORE_SUCCESS,
  LOAD_MORE_START,
}

interface LoadStart {
  type: Actions.LOAD_START
}
interface LoadMoreStart {
  type: Actions.LOAD_MORE_START
}

interface LoadSuccess {
  type: Actions.LOAD_SUCCESS
  list: IEmployee[]
  count: number
  reqId: number
  pageInfo: {
    endCursor: string
    hasNextPage: boolean
  }
}

interface SearchAction {
  type: Actions.SEARCH
  query: string
  reqId: number
}

interface LoadMoreAction {
  type: Actions.LOAD_MORE_SUCCESS
  list: IEmployee[]
  count: number
  reqId: number
  pageInfo: {
    endCursor: string
    hasNextPage: boolean
  }
}

type RootAction = LoadStart | LoadSuccess | SearchAction | LoadMoreAction | LoadMoreStart

interface IState {
  list: IEmployee[]
  isLoading: boolean
  isPaging: boolean
  query: string
  count: number
  reqId: number
  pageInfo: {
    hasNextPage: boolean
    endCursor: string
  }
  after: string
  first: number
}

export interface UseNeighborhoodEmployeesHook extends IState {
  id: string
  run: (query: string) => void
  loadMore: () => void
}

const initialState = {
  list: [],
  isLoading: true,
  isPaging: false,
  query: '',
  reqId: 0,
  count: 0,
  pageInfo: {
    hasNextPage: false,
    endCursor: '',
  },
  first: 5,
  after: '',
}

function reducer(state: IState, action: RootAction): IState {
  switch (action.type) {
    case Actions.LOAD_SUCCESS:
      return state.reqId > action.reqId
        ? state
        : {
            ...state,
            isLoading: false,
            list: action.list,
            count: action.count,
            pageInfo: action.pageInfo,
          }

    case Actions.LOAD_START:
      return {
        ...state,
        isLoading: true,
      }

    case Actions.SEARCH:
      return {
        ...state,
        reqId: action.reqId,
        query: action.query,
        isLoading: true,
      }

    case Actions.LOAD_MORE_START:
      return {
        ...state,
        isPaging: true,
      }

    case Actions.LOAD_MORE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        first: state.first + initialState.first,
        count: action.count,
        pageInfo: action.pageInfo,
        isPaging: false,
        list: [...state.list, ...action.list],
      }

    default:
      return state
  }
}

export default function useNeighborhoodEmployees(id: string): UseNeighborhoodEmployeesHook {
  const [state, dispatch] = useReducer(reducer, initialState)

  const shouldReload = useShouldReload()

  const debouncedSearch = useCallback(
    debounce((query: string, reqId: number) => {
      dispatch({ type: Actions.LOAD_START })

      api.neighborhood
        .employees({
          id,
          query,
          first: state.first,
        })
        .then((nh) => {
          dispatch({
            type: Actions.LOAD_SUCCESS,
            list: nh.employeeConnection.edges.map((i) => i.node),
            count: nh.employeeConnection.counters.total,
            pageInfo: nh.employeeConnection.pageInfo,
            reqId,
          })
        })
        .catch((error: ApolloError) => {
          Toaster.fail({ error })
        })
    }, 300),
    [state.first]
  )

  useEffect(() => {
    const reqId = Date.now()

    if (state.query) {
      debouncedSearch(state.query, reqId)
    }

    return () => debouncedSearch.cancel()
    // eslint-disable-next-line
  }, [state.query, debouncedSearch])

  useEffect(() => {
    if (!state.query) {
      api.neighborhood
        .employees({
          id,
          query: state.query,
          first: state.first,
        })
        .then((nh) => {
          dispatch({
            type: Actions.LOAD_SUCCESS,
            list: nh.employeeConnection.edges.map((i) => i.node),
            count: nh.employeeConnection.counters.total,
            pageInfo: nh.employeeConnection.pageInfo,
            reqId: Date.now(),
          })
        })
        .catch((error: ApolloError) => {
          Toaster.fail({ error })
        })
    }
    // eslint-disable-next-line
  }, [shouldReload.neighborhoods, state.query, id])

  return {
    ...state,
    id,
    run: (query: string) => {
      dispatch({
        type: Actions.SEARCH,
        query,
        reqId: Date.now(),
      })
    },
    loadMore: () => {
      dispatch({
        type: Actions.LOAD_MORE_START,
      })

      api.neighborhood
        .employees({
          id,
          query: state.query,
          first: initialState.first,
          after: state.pageInfo.endCursor,
        })
        .then((nh) => {
          dispatch({
            type: Actions.LOAD_MORE_SUCCESS,
            list: nh.employeeConnection.edges.map((i) => i.node),
            count: nh.employeeConnection.counters.total,
            pageInfo: nh.employeeConnection.pageInfo,
            reqId: Date.now(),
          })
        })
        .catch((error: ApolloError) => {
          Toaster.fail({ error })
        })
    },
  }
}
